import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { collectorsService } from '../services/collectors';

interface SchemaProperty {
  type: string;
  description?: string;
  required?: boolean;
  sensitive?: boolean;
  default?: any;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  format?: string;
  items?: {
    type: string;
  };
}

interface CollectorSchema {
  type: string;
  properties: Record<string, SchemaProperty>;
  required: string[];
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  connectivity_test: {
    success: boolean;
    message: string;
  };
}

interface CollectorConfigFormProps {
  collectorType: string;
  initialConfig?: any;
  onSave: (config: any) => void;
  onCancel: () => void;
}

const CollectorConfigForm: React.FC<CollectorConfigFormProps> = ({
  collectorType,
  initialConfig = {},
  onSave,
  onCancel,
}) => {
  const [schema, setSchema] = useState<CollectorSchema | null>(null);
  const [config, setConfig] = useState<any>(initialConfig);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showSensitive, setShowSensitive] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);

  const loadSchema = useCallback(async () => {
    try {
      setLoading(true);
      const schemaData = await collectorsService.getCollectorSchema(collectorType);
      setSchema(schemaData);

      // Initialize config with default values
      const defaultConfig = { ...initialConfig };
      Object.entries(schemaData.properties).forEach(([key, prop]) => {
        const property = prop as SchemaProperty;
        if (property.default !== undefined && defaultConfig[key] === undefined) {
          defaultConfig[key] = property.default;
        }
      });
      setConfig(defaultConfig);
    } catch (error) {
      console.error('Error loading schema:', error);
    } finally {
      setLoading(false);
    }
  }, [collectorType, initialConfig]);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  const validateConfig = async () => {
    if (!schema) return;

    setIsValidating(true);
    try {
      const result = await collectorsService.validateCollectorConfig(collectorType, config);
      setValidation(result);
    } catch (error) {
      console.error('Error validating config:', error);
      setValidation({
        valid: false,
        errors: ['Erro ao validar configuração'],
        warnings: [],
        connectivity_test: {
          success: false,
          message: 'Erro na validação',
        },
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfigChange = (key: string, value: any) => {
    setConfig((prev: Record<string, any>) => ({ ...prev, [key]: value }));
    setValidation(null); // Clear validation when config changes
  };

  const toggleSensitiveVisibility = (key: string) => {
    setShowSensitive(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderField = (key: string, property: any) => {
    const isRequired = schema?.required.includes(key);
    const isSensitive = property.sensitive;
    const showValue = !isSensitive || showSensitive[key];

    return (
      <div key={key} className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
          {key}
          {isRequired && <span className='text-red-500 ml-1'>*</span>}
        </label>

        {property.description && (
          <p className='text-xs text-gray-500 flex items-center gap-1'>
            <InformationCircleIcon className='h-3 w-3' />
            {property.description}
          </p>
        )}

        <div className='relative'>
          {property.type === 'string' && property.enum ? (
            <select
              value={config[key] || ''}
              onChange={e => handleConfigChange(key, e.target.value)}
              className='block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value=''>Selecione uma opção</option>
              {property.enum?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : property.type === 'boolean' ? (
            <div className='flex items-center'>
              <input
                type='checkbox'
                checked={config[key] || false}
                onChange={e => handleConfigChange(key, e.target.checked)}
                className='h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
              />
              <span className='ml-2 text-sm text-gray-700 dark:text-gray-300'>Habilitado</span>
            </div>
          ) : property.type === 'number' || property.type === 'integer' ? (
            <input
              type='number'
              value={config[key] || ''}
              onChange={e =>
                handleConfigChange(
                  key,
                  property.type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value),
                )
              }
              min={property.minimum}
              max={property.maximum}
              className='block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          ) : property.type === 'array' ? (
            <textarea
              value={Array.isArray(config[key]) ? config[key].join('\n') : config[key] || ''}
              onChange={e =>
                handleConfigChange(
                  key,
                  e.target.value.split('\n').filter(line => line.trim()),
                )
              }
              rows={4}
              placeholder='Um item por linha'
              className='block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          ) : (
            <input
              type={isSensitive && !showValue ? 'password' : 'text'}
              value={config[key] || ''}
              onChange={e => handleConfigChange(key, e.target.value)}
              className='block w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder={property.default ? `Padrão: ${property.default}` : ''}
            />
          )}

          {isSensitive && (
            <button
              type='button'
              onClick={() => toggleSensitiveVisibility(key)}
              className='absolute inset-y-0 right-0 pr-3 flex items-center'
            >
              {showValue ? (
                <EyeSlashIcon className='h-4 w-4 text-gray-400' />
              ) : (
                <EyeIcon className='h-4 w-4 text-gray-400' />
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const getCollectorTypeInfo = (type: string) => {
    const info: Record<string, { name: string; description: string; docs: string | null }> = {
      grayhatwarfare: {
        name: 'GrayHat Warfare',
        description: 'Busca por arquivos expostos em buckets S3',
        docs: 'https://grayhatwarfare.com/api',
      },
      subdomain_discovery: {
        name: 'Subdomain Discovery',
        description: 'Descoberta de subdomínios usando múltiplas fontes',
        docs: null,
      },
      phishing: {
        name: 'Phishing Collector',
        description: 'Coleta dados relacionados a phishing',
        docs: null,
      },
      nuclei: {
        name: 'Nuclei',
        description: 'Scanner de vulnerabilidades baseado em templates',
        docs: 'https://nuclei.projectdiscovery.io/',
      },
      dehashed: {
        name: 'DeHashed',
        description: 'Base de dados de credenciais vazadas',
        docs: 'https://dehashed.com/docs',
      },
    };

    return info[type] || { name: type, description: '', docs: null };
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500'>Erro ao carregar esquema de configuração</p>
      </div>
    );
  }

  const collectorInfo = getCollectorTypeInfo(collectorType);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='border-b border-gray-200 pb-4'>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white'>Configuração: {collectorInfo.name}</h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>{collectorInfo.description}</p>
        {collectorInfo.docs && (
          <a
            href={collectorInfo.docs}
            target='_blank'
            rel='noopener noreferrer'
            className='text-sm text-blue-600 hover:text-blue-800 mt-1 inline-block'
          >
            📖 Documentação da API
          </a>
        )}
      </div>

      {/* Configuration Form */}
      <div className='space-y-4'>
        {Object.entries(schema.properties).map(([key, property]) => renderField(key, property))}
      </div>

      {/* Validation Section */}
      <div className='border-t border-gray-200 pt-4'>
        <div className='flex items-center gap-3 mb-4'>
          <button
            onClick={validateConfig}
            disabled={isValidating}
            className='bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2'
          >
            {isValidating ? (
              <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
            ) : (
              <CheckCircleIcon className='h-4 w-4' />
            )}
            {isValidating ? 'Validando...' : 'Validar Configuração'}
          </button>
        </div>

        {validation && (
          <div className='space-y-3'>
            {/* Validation Status */}
            <div
              className={`p-3 rounded-md flex items-center gap-2 ${
                validation.valid ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              {validation.valid ? <CheckCircleIcon className='h-5 w-5' /> : <XCircleIcon className='h-5 w-5' />}
              <span className='font-medium'>{validation.valid ? 'Configuração válida' : 'Configuração inválida'}</span>
            </div>

            {/* Connectivity Test */}
            <div
              className={`p-3 rounded-md flex items-center gap-2 ${
                validation.connectivity_test.success ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'
              }`}
            >
              {validation.connectivity_test.success ? (
                <CheckCircleIcon className='h-5 w-5' />
              ) : (
                <ExclamationTriangleIcon className='h-5 w-5' />
              )}
              <div>
                <span className='font-medium'>
                  {validation.connectivity_test.success
                    ? 'Teste de conectividade passou'
                    : 'Teste de conectividade falhou'}
                </span>
                <p className='text-sm mt-1'>{validation.connectivity_test.message}</p>
              </div>
            </div>

            {/* Errors */}
            {validation.errors.length > 0 && (
              <div className='bg-red-50 p-3 rounded-md'>
                <h4 className='font-medium text-red-800 dark:text-red-200 mb-2'>Erros:</h4>
                <ul className='list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1'>
                  {validation.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {validation.warnings.length > 0 && (
              <div className='bg-yellow-50 p-3 rounded-md'>
                <h4 className='font-medium text-yellow-800 dark:text-yellow-200 mb-2'>Avisos:</h4>
                <ul className='list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1'>
                  {validation.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
        <button
          onClick={onCancel}
          className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200'
        >
          Cancelar
        </button>
        <button
          onClick={() => onSave(config)}
          disabled={validation ? !validation.valid : false}
          className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50'
        >
          Salvar Configuração
        </button>
      </div>
    </div>
  );
};

export default CollectorConfigForm;
